import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ProjectLayout, type NavItem } from '../components/projects';
import { EpisodeGrid, EpisodeFormDrawer, EpisodeFormData } from '../components/episodes';
import CreateScriptSection from '../components/ScriptGeneration/CreateScriptSection';
import Pagination from '../components/Pagination';
import { useEpisodesList } from '../hooks/useEpisodes';
import { episodeApi } from '../services/user/episode.api';
import { useToast } from '../stores/toastStore';
import { IEpisode } from '@packages/global';

export default function ProjectEpisodesPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation(['project', 'common']);

  const {
    episodes,
    loading,
    showForm,
    searchTerm,
    pagination,
    setShowForm,
    handleDelete,
    handlePageChange,
    loadEpisodes
  } = useEpisodesList(projectId!);

  const [showEditForm, setShowEditForm] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState<IEpisode | null>(null);

  const handlePageChangeWithProject = (page: number) => {
    if (projectId) {
      loadEpisodes({ projectId, search: searchTerm }, page, pagination.limit);
    } else {
      handlePageChange(page);
    }
  };

  const handleEditEpisode = (episode: IEpisode) => {
    setEditingEpisode(episode);
    setShowEditForm(true);
  };

  const handleSubmitEpisode = async (data: EpisodeFormData) => {
    if (!editingEpisode) return;
    try {
      await episodeApi.updateEpisode(editingEpisode.id, {
        title: data.title,
        description: data.description,
        thumbnailUrl: data.thumbnailUrl || undefined
      });
      await loadEpisodes({ projectId: projectId!, search: searchTerm }, pagination.page, pagination.limit);
      toast.success(t('project:toast.episodeUpdated'));
    } catch (error) {
      console.error('Failed to update episode:', error);
      toast.error(t('project:toast.episodeUpdateFailed'));
      throw error;
    }
  };

  const handleNavItemChange = (item: NavItem) => {
    if (!projectId) return;

    // 设定相关的导航，跳转到 settings 页面并传递 nav 参数
    const navType = item.type === 'setting' ? item.settingType : item.type;
    navigate(`/project/${projectId}/settings?nav=${navType}`, { replace: true });
  };

  return (
    <ProjectLayout activeTab="settings" activeNavItem={{ type: 'episodes' }} onNavItemChange={handleNavItemChange}>
      <div className="flex-1 overflow-auto p-6">
        <EpisodeGrid
          episodes={episodes}
          loading={loading}
          onCreateEpisode={() => setShowForm(true)}
          onEditEpisode={handleEditEpisode}
          onDeleteEpisode={handleDelete}
        />

        {pagination.totalPages > 1 && (
          <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-4 mt-6">
            <Pagination
              page={pagination.page}
              limit={pagination.limit}
              total={pagination.total}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChangeWithProject}
            />
          </div>
        )}
      </div>

      {/* Create Episode Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-4xl bg-bg-surface rounded-2xl shadow-2xl border border-border-subtle overflow-visible">
            <button
              onClick={() => setShowForm(false)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-50 hover:bg-bg-elevated rounded-full transition-all z-10 cursor-pointer"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            <div className="p-2 overflow-visible">
              <CreateScriptSection projectId={projectId} />
            </div>
          </div>
        </div>
      )}

      {/* Episode Edit Form Modal */}
      <EpisodeFormDrawer
        isOpen={showEditForm}
        editingEpisode={editingEpisode}
        onSubmit={handleSubmitEpisode}
        onClose={() => {
          setShowEditForm(false);
          setEditingEpisode(null);
        }}
      />
    </ProjectLayout>
  );
}
